import { BodyText, Button, Callout, Skeleton } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { useGetPatient } from "@/features/Patient/network/usePatient";
import { useEnvironment } from "@/hooks/useEnvironment";
import { trackEvent } from "@/lib/tracking";
import { useParams } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useState } from "react";
import { SectionHeader } from "../components/SectionHeader";
import { useHiePullStatus } from "./network/useHiePullStatus";
import { useMetriportEmbedToken } from "./network/useMetriportEmbedToken";

export const METRIPORT_IDENTIFIER_SYSTEM = "https://api.metriport.com";

const METRIPORT_PROD_EMBED_HOST = "https://ehr.metriport.com";
const METRIPORT_SANDBOX_EMBED_HOST = "https://ehr.sandbox.metriport.com";

const buildMetriportPatientEmbedUrl = (
  host: string,
  metriportId: string,
  token: string,
) => `${host}/embed/app/patient/${metriportId}#access_token=${token}`;

export const MetriportHIE = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const {
    data: patient,
    isLoading: isLoadingPatient,
    isError: isPatientError,
  } = useGetPatient(patientId);
  const metriportId = patient?.getIdentifier(METRIPORT_IDENTIFIER_SYSTEM);
  const hasHieConsent = patient?.hasHieConsent ?? false;
  const { isProd } = useEnvironment();
  const embedHost = isProd
    ? METRIPORT_PROD_EMBED_HOST
    : METRIPORT_SANDBOX_EMBED_HOST;

  const {
    data: pullStatus,
    isLoading: isLoadingPullStatus,
    isError: isPullStatusError,
  } = useHiePullStatus(patientId, Boolean(metriportId) && hasHieConsent);

  const {
    data: tokenData,
    isLoading: isLoadingToken,
    isError: isTokenError,
  } = useMetriportEmbedToken(
    Boolean(metriportId) && hasHieConsent && pullStatus?.inProgress === false,
  );

  const embedUrl =
    metriportId && tokenData?.token
      ? buildMetriportPatientEmbedUrl(embedHost, metriportId, tokenData.token)
      : null;

  // Keep the skeleton up until the iframe itself paints — react-query stops
  // "loading" when the token arrives, but the iframe still needs to fetch
  // Metriport's app over the network. Tracking by token (rather than a plain
  // boolean) means a token rotation naturally re-shows the skeleton until
  // the remounted iframe fires onLoad with the new token.
  const [loadedToken, setLoadedToken] = useState<string | undefined>();
  const isIframeLoaded = Boolean(
    tokenData?.token && loadedToken === tokenData.token,
  );

  const renderContent = () => {
    if (isLoadingPatient || isLoadingToken || isLoadingPullStatus) {
      return <Skeleton className="w-full flex-1 min-h-0" />;
    }

    if (isPatientError || isTokenError || isPullStatusError) {
      return (
        <Callout
          className="w-full"
          variant="danger"
          title="Failed to load HIE data"
          description="Please refresh and try again."
        />
      );
    }

    if (!metriportId) {
      return (
        <BodyText size="sm" textColor="tertiary" className="py-2">
          No HIE data is available for this patient.
        </BodyText>
      );
    }

    if (!hasHieConsent) {
      return (
        <BodyText size="sm" textColor="tertiary" className="py-2">
          This patient has not consented to HIE data sharing.
        </BodyText>
      );
    }

    if (pullStatus?.inProgress) {
      return (
        <BodyText size="sm" textColor="tertiary" className="py-2">
          An HIE data pull is in progress. This view will update automatically
          when it completes.
        </BodyText>
      );
    }

    if (!embedUrl) {
      return <Skeleton className="w-full flex-1 min-h-0" />;
    }

    return (
      <div className="relative w-full flex-1 min-h-0">
        {!isIframeLoaded && (
          <Skeleton className="absolute inset-0 w-full h-full" />
        )}
        <iframe
          // Remount the iframe whenever the token refreshes so the embed
          // is always loaded with a non-expired token.
          key={tokenData?.token}
          title="HIE Data Patient View"
          src={embedUrl}
          data-testid="metriport-hie-iframe"
          onLoad={() => setLoadedToken(tokenData?.token)}
          className="w-full h-full border-0"
        />
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <SectionHeader
        title="HIE Data"
        actionButtons={
          embedUrl
            ? [
                <Button
                  key="open-in-new-tab"
                  variant="secondary"
                  size="sm"
                  leftIcon={ExternalLink}
                  onClick={() => {
                    trackEvent(TRACKERS.OPEN_HIE_DATA_IN_NEW_TAB);
                    window.open(embedUrl, "_blank", "noopener,noreferrer");
                  }}
                >
                  Open in new tab
                </Button>,
              ]
            : undefined
        }
      />
      <div className="flex-1 min-h-0 flex flex-col">{renderContent()}</div>
    </div>
  );
};
